import { useState, useCallback, useEffect, useRef } from "react"
import { conciergeClient } from "./concierge-client"
import type { ConciergeMessage, ProactiveMessage } from "./types"

const STORAGE_KEY_PREFIX = "concierge:"
const STALE_MS = 24 * 60 * 60 * 1000 // 24 hours

function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

function loadMessages(key: string): ConciergeMessage[] {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${key}:messages`)
    if (!raw) return []
    const parsed = JSON.parse(raw) as Array<ConciergeMessage & { timestamp: string }>
    // Check staleness
    const lastMsg = parsed[parsed.length - 1]
    if (lastMsg && Date.now() - new Date(lastMsg.timestamp).getTime() > STALE_MS) {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}${key}:messages`)
      return []
    }
    return parsed.map((m) => ({ ...m, timestamp: new Date(m.timestamp) }))
  } catch {
    return []
  }
}

function saveMessages(key: string, messages: ConciergeMessage[]): void {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${key}:messages`, JSON.stringify(messages))
  } catch {
    // Silently ignore storage errors
  }
}

export function useConcierge(propertyId: string | undefined) {
  const [messages, setMessages] = useState<ConciergeMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [proactiveMessage, setProactiveMessage] = useState<ProactiveMessage | null>(null)
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Storage key uses propertyId for per-property conversation isolation
  const storageKey = propertyId || "global"

  // Load persisted messages on mount
  useEffect(() => {
    setMessages(loadMessages(storageKey))
  }, [storageKey])

  // Save messages on change
  useEffect(() => {
    if (messages.length > 0) {
      saveMessages(storageKey, messages)
    }
  }, [storageKey, messages])

  // Poll for proactive messages (only when panel is closed)
  useEffect(() => {
    const poll = async () => {
      if (isPanelOpen) return
      const res = await conciergeClient.getProactive()
      if (res.success && res.data?.has_message && res.data.action_id) {
        setProactiveMessage({
          action_id: res.data.action_id,
          action_type: res.data.action_type || "insight",
          priority: res.data.priority || "medium",
          title: res.data.title || "New message",
          body: res.data.body || "",
          cta: res.data.cta || "View details",
          badge_type: res.data.badge_type || "insight",
        })
      }
    }

    // Initial poll
    poll()

    // Poll every 60s
    pollRef.current = setInterval(poll, 60_000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [isPanelOpen])

  const sendMessage = useCallback(
    async (text: string) => {
      // Add user message optimistically
      const userMsg: ConciergeMessage = {
        id: generateId(),
        role: "user",
        content: text,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, userMsg])
      setIsLoading(true)

      try {
        const res = await conciergeClient.sendMessage(text, {
          current_page: window.location.pathname,
          property_id: propertyId,
        })

        if (res.success && res.data) {
          const assistantMsg: ConciergeMessage = {
            id: generateId(),
            role: "assistant",
            content: res.data.message,
            timestamp: new Date(),
            quickActions: res.data.quick_actions,
            insightCard: res.data.insight_card
              ? {
                  type: res.data.insight_card.type,
                  label: res.data.insight_card.label,
                  value: res.data.insight_card.value,
                  valueColor: res.data.insight_card.value_color,
                  description: res.data.insight_card.description,
                  link: {
                    text: res.data.insight_card.link_text,
                    href: res.data.insight_card.link_href,
                  },
                }
              : undefined,
          }
          setMessages((prev) => [...prev, assistantMsg])
        } else {
          // Error response — show as assistant message
          const errorMsg: ConciergeMessage = {
            id: generateId(),
            role: "assistant",
            content: "Sorry, I ran into an issue. Please try again in a moment.",
            timestamp: new Date(),
          }
          setMessages((prev) => [...prev, errorMsg])
        }
      } catch {
        const errorMsg: ConciergeMessage = {
          id: generateId(),
          role: "assistant",
          content: "Sorry, I couldn't connect. Please check your connection and try again.",
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, errorMsg])
      } finally {
        setIsLoading(false)
      }
    },
    [propertyId],
  )

  const openPanel = useCallback(() => setIsPanelOpen(true), [])
  const closePanel = useCallback(() => setIsPanelOpen(false), [])

  const dismissProactive = useCallback(async () => {
    if (!proactiveMessage) return
    setProactiveMessage(null)
    await conciergeClient.dismissProactive(proactiveMessage.action_id)
  }, [proactiveMessage])

  const openProactive = useCallback(async () => {
    if (!proactiveMessage) return
    const msg = proactiveMessage
    setProactiveMessage(null)
    setIsPanelOpen(true)

    const res = await conciergeClient.openProactive(msg.action_id)
    if (res.success && res.data?.content?.body) {
      const seedMsg: ConciergeMessage = {
        id: generateId(),
        role: "assistant",
        content: res.data.content.body,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, seedMsg])
    }
  }, [proactiveMessage])

  return {
    messages,
    isLoading,
    proactiveMessage,
    isPanelOpen,
    sendMessage,
    openPanel,
    closePanel,
    dismissProactive,
    openProactive,
  }
}
